import { openDB, DBSchema, IDBPDatabase, IDBPTransaction, deleteDB } from "idb";
import {
  BibleSiteGettable,
  BibleSiteSettable
} from "./typedefs/BibleSiteStorage";
import {
  OfflineState,
  Site,
  Bible,
  Book,
  Chapter
} from "./typedefs/BibleOffline";
import { Status, LATER, NOT_FOUND, LOCAL } from "./typedefs/RequestStatus";
import { Storable } from "./typedefs/GenericStorage";

//===== SCHEMA

const dbVersion = 1; // schema version for migrations.
const dbName = "bibleSites";
export const delay = (ms: number) =>
  new Promise(resolve => setTimeout(resolve, ms));

export interface SiteSchema extends DBSchema {
  sites: { key: string; value: Site };
  bibles: { key: string; value: Bible };
  // books: { key: string; value: Book[] }; // store these within the Bible for now
  chapters: { key: string[]; value: Chapter };
  config: { key: string; value: any };
}

export type SiteCollectionName = "sites" | "bibles" | "chapters" | "config";

// This is the upgrade function run when an IndexedDb is opened
async function runMigrations(
  db: IDBPDatabase<SiteSchema>,
  oldVersion: number,
  newVersion: number | null,
  transaction: IDBPTransaction<SiteSchema, SiteCollectionName[]>
): Promise<void> {
  if (oldVersion < 1) {
    db.createObjectStore("sites", { keyPath: "domain" });
    db.createObjectStore("bibles", { keyPath: "id" });
    db.createObjectStore("chapters", { keyPath: ["bibleId", "id"] });
    db.createObjectStore("config");
    const config = transaction.objectStore("config");
    await config.put(newVersion, "migrationVersion");
    await config.put(new Date().toISOString(), "lastMigration");
  }
}

//===== BIBLE SITE STORAGE

export class IndexedDb
  implements BibleSiteGettable, BibleSiteSettable, Storable {
  protected db?: IDBPDatabase<SiteSchema>;
  protected isOpen = false;

  // ----- STATIC

  static capable = () => !!(window && window.indexedDB);

  //----- LIFECYCLE METHODS

  constructor(private databaseName = dbName) {
    if (!IndexedDb.capable()) {
      throw new Error("this platform doesn't support IndexedDb");
    }
  }

  close() {
    this.isOpen = false;
    this.db && this.db.close();
  }

  async open() {
    if (this.isOpen) return;
    this.db = await openDB(this.databaseName, dbVersion, {
      upgrade: runMigrations
    });
  }

  // upgrade runs migrations as needed
  private async upgrade(
    db: IDBPDatabase<SiteSchema>,
    oldVersion: number,
    newVersion: number | null,
    transaction: IDBPTransaction<SiteSchema, SiteCollectionName[]>
  ): Promise<void> {
    if (oldVersion < 1) {
      db.createObjectStore("sites", { keyPath: "domain" });
      db.createObjectStore("bibles", { keyPath: "id" });
      db.createObjectStore("chapters", { keyPath: ["bibleId", "id"] });
      db.createObjectStore("config");
      const config = transaction.objectStore("config");
      await config.put(newVersion, "migrationVersion");
      await config.put(new Date().toISOString(), "lastMigration");
    }
  }

  // careful with this--it's mostly for testing
  rawDb() {
    return this.db;
  }

  //----- SITE GETTABLE METHODS

  async getSite(
    fullSiteDomain: string
  ): Promise<{ site: Site | undefined; status: Status }> {
    let status = NOT_FOUND;
    const site = this.db && (await this.db.get("sites", fullSiteDomain));
    if (site) {
      // TODO: some site records only have subdomain set. If so, what are the implications (especially for GA)?
      site.domain = fullSiteDomain;
      status = LOCAL;
    }
    return { site, status };
  }

  async getBible(
    bibleId: string
  ): Promise<{ bible: Bible | undefined; status: Status }> {
    const bible = this.db && (await this.db.get("bibles", bibleId));
    const status = bible ? LOCAL : NOT_FOUND;
    return { bible, status };
  }

  async getBooks(
    bibleId: string
  ): Promise<{ books: Book[] | undefined; status: Status }> {
    const bible = this.db && (await this.db.get("bibles", bibleId));
    const books =
      bible && bible.books && bible.books.length ? bible.books : undefined;
    const status = books ? LOCAL : NOT_FOUND;
    return { books, status };
  }

  async getChapter(
    bibleId: string,
    chapterId: string
  ): Promise<{ chapter: Chapter | undefined; status: Status }> {
    const chapter =
      this.db && (await this.db.get("chapters", [bibleId, chapterId]));
    const status = chapter ? LOCAL : LATER;
    return { chapter, status };
  }

  //----- SITESETABLE METHODS

  async setSite(site: Site): Promise<boolean> {
    if (!this.db) throw new Error("open the database first");
    if (!site.domain) new Error("site.domain must not be empty");
    await this.db.put("sites", site);
    return true;
  }

  async setBible(bible: Bible): Promise<boolean> {
    return !!(this.db && (await this.db.put("bibles", bible)));
  }

  async removeBible(bibleId: string): Promise<boolean> {
    throw new Error("not implemented");
  }

  async setBooks(bibleId: string, books: Book[]): Promise<boolean> {
    const bible = this.db && (await this.db.get("bibles", bibleId));
    if (!bible) return false;
    bible.books = books;
    bible.offlineState = OfflineState.local;
    return !!(this.db && this.db.put("bibles", bible));
  }

  async setChapter(chapter: Chapter): Promise<boolean> {
    chapter.offlineState = chapter.deepOfflineState = OfflineState.local; // deep because we always download with text
    return !!(this.db && (await this.db.put("chapters", chapter)));
  }

  //----- STORABLE IMPLEMENTATION METHODS

  async get(
    collectionName: SiteCollectionName,
    keys: string[]
  ): Promise<{ value: any; status: Status }> {
    let value = undefined,
      status = NOT_FOUND;
    if (this.db)
      ({ value, status } = await this.db.get(collectionName, keys.join(".")));
    return { value, status };
  }

  async set(
    collectionName: SiteCollectionName,
    keys: string[],
    value: any
  ): Promise<boolean> {
    return !!(this.db && (await this.db.put(collectionName, keys.join("."))));
  }

  async remove(
    collectionName: SiteCollectionName,
    keys: string[]
  ): Promise<boolean> {
    if (!this.db) return false;
    await this.db.delete(collectionName, keys.join("."));
    return true;
  }

  // BUG: this is not reliable--usually the idb store can't close and won't delete.
  // The blocked() callback is supposed to offer a workaround for this, but
  // doesn't work consistently (at least in Chrome July 2019).
  async clear(attempts = 0) {
    if (attempts > 3) return false; // stop trying to do it, it's blocked
    this.close();
    await delay(500);
    await deleteDB(this.databaseName, {
      blocked: async () => {
        await delay(500);
        this.clear(attempts + 1);
      }
    });
    await this.open();
    return true;
  }
}
